import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { ApiService } from '../../api.service';
import { LivekitService } from '../../services/livekit.service';
import { CallService } from '../../services/call.service';
import { RoomEvent, RemoteParticipant, RemoteTrackPublication, Track, Room } from 'livekit-client';

@Component({
  selector: 'app-video-call',
  templateUrl: './video-call.component.html',
  styleUrls: ['./video-call.component.css']
})
export class VideoCallComponent implements OnInit, OnDestroy {
  @ViewChild('localVideo') localVideoRef!: ElementRef<HTMLVideoElement>;
  @ViewChild('remoteContainer') remoteContainerRef!: ElementRef<HTMLDivElement>;

  isMuted = false;
  isCameraOff = false;
  inCall = false;
  connecting = true;
  error = '';
  currentRoomName = '';
  hasRemoteParticipant = false;
  remoteCount = 0;

  showAdd = false;
  friends: { id: number; name: string }[] = [];
  selected = new Set<number>();
  
  loadingFriends = false;

  constructor(
    private livekitService: LivekitService,
    private callService: CallService,
    private apiService: ApiService,
    private route: ActivatedRoute,
    private location: Location
  ) {}

  ngOnInit() {
    this.route.paramMap.subscribe(p => {
      const room = p.get('roomName');
      if (!room) { this.location.back(); return; }
      if (room === this.currentRoomName) return;
      this.cleanup();                       // naye room par jate waqt purana band
      this.currentRoomName = room;
      this.startCall(room);
    });
  }

  async startCall(roomName: string) {
    this.connecting = true;
    this.error = '';
    try {
      const res = await this.livekitService.getToken(roomName, 'caller');
      const room = new Room();

      room.on(RoomEvent.TrackSubscribed, (track: Track, _p: RemoteTrackPublication, participant: RemoteParticipant) => {
        this.attachRemoteTrack(track, participant);
        this.checkRemote(room);
      });
      room.on(RoomEvent.TrackUnsubscribed, (track: Track) => track.detach().forEach(el => el.remove()));
      room.on(RoomEvent.ParticipantDisconnected, () => this.checkRemote(room));
      room.on(RoomEvent.Disconnected, () => (this.inCall = false));

      await room.connect(res.server_url, res.token);
      (this.livekitService as any).room = room;
      await room.localParticipant.enableCameraAndMicrophone();
      this.inCall = true;
      this.connecting = false;

      const lv = room.localParticipant.videoTrackPublications.values().next().value?.videoTrack;
      if (lv) lv.attach(this.localVideoRef.nativeElement);

      room.remoteParticipants.forEach(p =>
        p.trackPublications.forEach(pub => { if (pub.track) this.attachRemoteTrack(pub.track, p); })
      );
      this.checkRemote(room);
    } catch (e) {
      this.connecting = false;
      this.error = 'Could not join the call. Please try again.';
    }
  }

  attachRemoteTrack(track: Track, participant: RemoteParticipant) {
    if (track.kind !== Track.Kind.Video && track.kind !== Track.Kind.Audio) return;
    if (document.getElementById(`track-${track.sid}`)) return;
    const el = track.attach();
    el.id = `track-${track.sid}`;
    el.setAttribute('data-participant', participant.identity);
    this.remoteContainerRef.nativeElement.appendChild(el);
  }

  checkRemote(room: Room) {
    this.remoteCount = room.remoteParticipants.size;
    this.hasRemoteParticipant = this.remoteCount > 0;
  }

  toggleMute() {
    const room = this.livekitService.room;
    if (!room) return;
    this.isMuted = !this.isMuted;
    room.localParticipant.setMicrophoneEnabled(!this.isMuted);
  }

  toggleCamera() {
    const room = this.livekitService.room;
    if (!room) return;
    this.isCameraOff = !this.isCameraOff;
    room.localParticipant.setCameraEnabled(!this.isCameraOff);
  }


  openAdd() {
    this.showAdd = true;
    if (this.friends.length) return;        // pehle se load hain

    this.loadingFriends = true;
    const userId: any = localStorage.getItem('user_id');
    this.apiService.getContact(userId).subscribe({
      next: (res: any) => {
        this.friends = (res.contacts || []).map((c: any) => ({
          id: c.user.id,
          name: c.user.firstname + ' ' + c.user.lastname
        }));
        this.loadingFriends = false;
      },
      error: () => {
        this.friends = [];
        this.loadingFriends = false;
      }
    });
  }

  toggleSelect(id: number) {
    this.selected.has(id) ? this.selected.delete(id) : this.selected.add(id);
  }

  async addSelected() {
    if (!this.selected.size) return;
    await this.callService.invite([...this.selected], this.currentRoomName);
    this.selected.clear();
    this.showAdd = false;
  }

  endCall() {
    this.cleanup();
    this.location.back();
  }

  private cleanup() {
    this.livekitService.leaveRoom();
    this.inCall = false;
    this.hasRemoteParticipant = false;
    this.remoteCount = 0;
    if (this.remoteContainerRef) this.remoteContainerRef.nativeElement.innerHTML = '';
  }

  ngOnDestroy() { this.cleanup(); }
}